import { intl } from 'lang/intl'
import React, { useCallback } from 'react'
import { FormattedMessage } from 'react-intl'

import { TtTooltip } from '@23/react'

import TtFormik from '../../../global/formik-forms'
import { getLiveId } from '../../../global/blocs/roomInfo'
import { useModal } from '../../../Layout/Modal'
import {
  enableFocusMode,
  type FocusPane,
  setFocusMode,
} from '../../blocs/useFocusMode'

import { FocusPanelMode } from './FocusPanelMode/FocusPanelMode'
import { FocusPreview } from './FocusPreview/FocusPreview'

import './FocusConfigurator.scss'

type FieldValues = {
  leftPanelMode: FocusPane['mode'] | 'empty'
  leftPanelParticipantIds: undefined | []
  leftPanelScreenIds: undefined | []
  rightPanelMode: FocusPane['mode'] | 'empty'
  rightPanelParticipantIds: undefined | []
  rightPanelScreenIds: undefined | []
  liveTimer: boolean
  timer: boolean
  clock: boolean
  stopwatch: boolean
  audienceCount: boolean
  reactions: boolean
}

const getStoredConfig = () => {
  const webinarConfig = localStorage.getItem(`tt:focusConfig:${getLiveId()}`)
  if (webinarConfig) return JSON.parse(webinarConfig)

  const globalConfig = localStorage.getItem('tt:focusConfig:Global')
  if (globalConfig) return JSON.parse(globalConfig)

  return {
    leftPanelMode: 'tray-slides',
    leftPanelParticipantIds: undefined,
    leftPanelScreenIds: undefined,
    rightPanelMode: 'tray-notes',
    rightPanelParticipantIds: undefined,
    rightPanelScreenIds: undefined,
    liveTimer: true,
    timer: true,
    clock: true,
    stopwatch: false,
    audienceCount: true,
    reactions: false,
  }
}

const storeConfig = (fieldValues: FieldValues) => {
  const values = JSON.stringify(fieldValues)
  localStorage.setItem(`tt:focusConfig:${getLiveId()}`, values)
  localStorage.setItem('tt:focusConfig:Global', values)
}

const FocusConfigurator = () => {
  const { closeModal } = useModal()

  const handleSubmit = useCallback(
    (fieldValues: FieldValues) => {
      const panesConfig = [] as FocusPane[]

      if (fieldValues.leftPanelMode !== 'empty') {
        panesConfig.push({
          mode: fieldValues.leftPanelMode,
          participantIds: fieldValues.leftPanelParticipantIds ?? [],
          screenIds: fieldValues.leftPanelScreenIds ?? [],
        })
      }

      if (fieldValues.rightPanelMode !== 'empty') {
        panesConfig.push({
          mode: fieldValues.rightPanelMode,
          participantIds: fieldValues.rightPanelParticipantIds ?? [],
          screenIds: fieldValues.rightPanelScreenIds ?? [],
        })
      }

      setFocusMode(panesConfig, {
        liveTimer: fieldValues.liveTimer,
        timer: fieldValues.timer,
        clock: fieldValues.clock,
        stopwatch: fieldValues.stopwatch,
        audienceCount: fieldValues.audienceCount,
        reactions: fieldValues.reactions,
      })
      storeConfig(fieldValues)

      closeModal()
      enableFocusMode()
    },
    [closeModal]
  )

  return (
    <>
      <div className="focus-configurator">
        <p>
          <FormattedMessage defaultMessage="Create a focus on the things you need in the moment" />
        </p>

        <TtFormik.Form<FieldValues>
          label={intl.formatMessage({
            defaultMessage: 'Focus Mode Configuration',
          })}
          initialValues={getStoredConfig()}
          onSubmit={handleSubmit}
        >
          {(formik) => (
            <>
              <FocusPreview configuration={formik.values} />

              <TtFormik.Fieldset
                name="panels"
                direction="horizontal"
                hideLabel
                label={intl.formatMessage({
                  defaultMessage: 'Panels Configuration',
                })}
              >
                <FocusPanelMode side="left" configuration={formik.values} />

                <FocusPanelMode side="right" configuration={formik.values} />
              </TtFormik.Fieldset>

              <TtFormik.Fieldset
                name="panels"
                direction="vertical"
                label={intl.formatMessage({ defaultMessage: 'Toolbar' })}
              >
                <div className="focus-configurator-widgets">
                  <TtTooltip
                    content={intl.formatMessage({
                      defaultMessage: 'How long the webinar has been live for',
                    })}
                    placement="top-end"
                    hoist
                  >
                    <TtFormik.Toggle<FieldValues>
                      name="liveTimer"
                      label={intl.formatMessage({
                        defaultMessage: 'Time live',
                      })}
                    />
                  </TtTooltip>
                  <TtTooltip
                    content={intl.formatMessage({
                      defaultMessage:
                        'A countdown until the end of the webinar',
                    })}
                    placement="top-end"
                    hoist
                  >
                    <TtFormik.Toggle<FieldValues>
                      name="timer"
                      label={intl.formatMessage({
                        defaultMessage: 'Webinar Timer',
                      })}
                    />
                  </TtTooltip>
                  <TtTooltip
                    content={intl.formatMessage({
                      defaultMessage: 'Current time of the day',
                    })}
                    placement="top-end"
                    hoist
                  >
                    <TtFormik.Toggle<FieldValues>
                      name="clock"
                      label={intl.formatMessage({ defaultMessage: 'Clock' })}
                    />
                  </TtTooltip>

                  <TtTooltip
                    content={intl.formatMessage({
                      defaultMessage:
                        'Start counting when you enter focus mode',
                    })}
                    placement="top-end"
                    hoist
                  >
                    <TtFormik.Toggle<FieldValues>
                      name="stopwatch"
                      label={intl.formatMessage({
                        defaultMessage: 'Stopwatch',
                      })}
                    />
                  </TtTooltip>

                  <TtTooltip
                    content={intl.formatMessage({
                      defaultMessage: 'Number of people assisting the webinar',
                    })}
                    placement="top-end"
                    hoist
                  >
                    <TtFormik.Toggle<FieldValues>
                      name="audienceCount"
                      label={intl.formatMessage({
                        defaultMessage: 'Participants',
                      })}
                    />
                  </TtTooltip>

                  <TtTooltip
                    content={intl.formatMessage({
                      defaultMessage: 'Coming soon',
                    })}
                    placement="top-end"
                    hoist
                  >
                    <TtFormik.Toggle<FieldValues>
                      name="reactions"
                      disabled
                      label={intl.formatMessage({
                        defaultMessage: 'Reactions',
                      })}
                    />
                  </TtTooltip>
                </div>
              </TtFormik.Fieldset>

              <TtFormik.SubmitButton disabled={false} pill>
                <FormattedMessage defaultMessage="Start Focus Mode" />
              </TtFormik.SubmitButton>
            </>
          )}
        </TtFormik.Form>
      </div>
    </>
  )
}

const useOpenFocusConfigurator = () => {
  const { openModal } = useModal()

  return useCallback(() => {
    openModal({
      label: intl.formatMessage({ defaultMessage: 'Start Focus Mode' }),
      body: <FocusConfigurator />,
      closable: true,
    })
  }, [openModal])
}

export {
  FocusConfigurator,
  useOpenFocusConfigurator,
  type FieldValues as FocusConfiguration,
}
